import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import { useChatStore } from "../store/useChatStore";
import type { ISetupPlanStep } from "../store/useChatStore";
import { useStore } from "../store/useStore";
import { summarizeSetupPlan } from "../logic/plan-summary";
import { PlanReadyScreen } from "./PlanReadyScreen";

/**
 * Watches the AI setup plan (read-only consumer of useChatStore) and shows
 * the PlanReadyScreen when a plan transitions to `completed` during this
 * session. Plans that were already completed when loaded are ignored.
 *
 * Counts are computed live from the store while the screen is visible, so
 * tasks that arrive (realtime/refetch) after completion are still counted.
 * They cover all tasks in the plan's rooms (matched by area name).
 */
export function PlanReadyWatcher() {
  const history = useHistory();
  const setupPlan = useChatStore((s) => s.setupPlan);
  const closeChat = useChatStore((s) => s.close);
  const areas = useStore((s) => s.areas);
  const tasks = useStore((s) => s.tasks);

  const lastPlan = useRef<{ uuid: string; status: string } | null>(null);
  const [completedSteps, setCompletedSteps] = useState<ISetupPlanStep[] | null>(null);

  useEffect(() => {
    if (!setupPlan) {
      lastPlan.current = null;
      return;
    }

    const previous =
      lastPlan.current?.uuid === setupPlan.uuid ? lastPlan.current.status : null;

    if (previous && previous !== "completed" && setupPlan.status === "completed") {
      setCompletedSteps(setupPlan.steps);
    }

    lastPlan.current = { uuid: setupPlan.uuid, status: setupPlan.status };
  }, [setupPlan]);

  const summary = useMemo(
    () => (completedSteps ? summarizeSetupPlan(completedSteps, areas, tasks) : null),
    [completedSteps, areas, tasks]
  );

  const handleClose = useCallback(() => setCompletedSteps(null), []);

  const handleViewTasks = useCallback(() => {
    setCompletedSteps(null);
    closeChat();
    history.push("/today");
  }, [closeChat, history]);

  return (
    <AnimatePresence>
      {summary && (
        <PlanReadyScreen
          spacesCount={summary.spacesCount}
          tasksCreated={summary.tasksCreated}
          estimatedMinutes={summary.estimatedMinutes}
          onViewTasks={handleViewTasks}
          onClose={handleClose}
        />
      )}
    </AnimatePresence>
  );
}

export default PlanReadyWatcher;
